import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import Select2Dropdown from '../common/Select2Dropdown';
import '../admin/ListsConfiguration.css';
import '../common/Dialog.css';

const API_URL = '/api/masters/accounts-settings/';
const INVOICE_CHARGE_NAMES_API = '/api/masters/invoice-charge-names/';
const STATEMENT_CHARGE_NAMES_API = '/api/masters/statement-charge-names/';

const DEFAULT_CHARGE_NAME = 'C&F AGENCY FEE';

const SHIPMENT_TYPES = [
    'IMPORT-SEA', 'IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-SEA', 'EXPORT-AIR', 'EXPORT-ROAD',
    'TRANSIT-SEA', 'TRANSIT-AIR', 'EX BOND'
];

const CARGO_TYPES = ['FCL', 'LCL', 'VEHICLE', 'BREAK BULK', 'EXPORT ENTRY', 'SCT', 'ROAD', 'AIR', 'AIR COURIER', 'ZNZ'];

const CONTAINER_SIZE_OPTIONS = ['20 ST', '40 ST', '40 HC', '45 HC'];

const WEIGHT_OPTIONS = ['0 - 100 KG', 'ABOVE 101 KG'];
const VEHICLE_OPTIONS = ['SALOON', 'HEAVY'];

const getFilteredCargoTypes = (shipmentType) => {
    const st = (shipmentType || '').toUpperCase();
    switch (st) {
        case 'IMPORT-SEA':
        case 'TRANSIT-SEA':
            return ['FCL', 'LCL', 'VEHICLE', 'BREAK BULK'];
        case 'EXPORT-SEA':
            return ['FCL', 'ZNZ'];
        case 'IMPORT-AIR':
        case 'EXPORT-AIR':
        case 'TRANSIT-AIR':
            return ['AIR', 'AIR COURIER'];
        case 'IMPORT-ROAD':
            return ['ROAD'];
        case 'EXPORT-ROAD':
            return ['EXPORT ENTRY', 'SCT'];
        case 'EX BOND':
            return ['VEHICLE', 'FCL'];
        default:
            return [];
    }
};

const sizeOrWeightLabel = (cargoType, shipmentType) => {
    const ct = (cargoType || '').toUpperCase();
    const st = (shipmentType || '').toUpperCase();
    if (ct === 'FCL') return 'Container Size';
    if (ct === 'VEHICLE') return 'Vehicle Type';
    if (st.includes('AIR')) return 'Weight';
    return 'Size / Weight';
};

const formatWithCommas = (val) => {
    if (val === '' || val === undefined || val === null) return '';
    const s = String(val).replace(/,/g, '');
    const [intPart, decPart] = s.split('.');
    if (!intPart) return '';
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${withCommas}.${decPart.slice(0, 2)}` : withCommas;
};

const parseAmount = (value) => {
    return String(value).replace(/,/g, '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
};

const parseAccounting = (value) => {
    const s = String(value || '');
    const isNegative = s.trim().startsWith('(') || s.trim().startsWith('-');
    const clean = s.replace(/[(),]/g, '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    return isNegative && clean ? `-${clean}` : clean;
};

const formatAccounting = (val) => {
    if (val === '' || val === undefined || val === null) return '';
    const num = Number(String(val).replace(/,/g, ''));
    if (isNaN(num)) return '';
    const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `(${formatted})` : formatted;
};

const AccountsSettings = () => {
    const { hasPermission: rawHasPermission } = usePermissions();
    const hasPermission = (module, action) => rawHasPermission(module, action);
    const canCreate = hasPermission('ACCOUNTS SETTINGS', 'can_create');
    const canEdit = hasPermission('ACCOUNTS SETTINGS', 'can_edit');
    const canDelete = hasPermission('ACCOUNTS SETTINGS', 'can_delete');

    const [customers, setCustomers] = useState([]);
    const [customersWithDefaults, setCustomersWithDefaults] = useState([]);
    const [customersWithOverrides, setCustomersWithOverrides] = useState([]);
    const [charges, setCharges] = useState([]);
    const [fundRequestIds, setFundRequestIds] = useState([]);
    const [fundRequestSelect, setFundRequestSelect] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customerCharges, setCustomerCharges] = useState([]);
    const [customerBalances, setCustomerBalances] = useState({
        previous_outstanding_balance: '',
        previous_outstanding_balance_usd: '',
        balance_brought_forward: '',
        balance_brought_forward_usd: '',
        balance_brought_forward_date: '',
        show_receivables: true
    });
    const [selectedBalanceCustomerId, setSelectedBalanceCustomerId] = useState('');
    const [openingSigns, setOpeningSigns] = useState({ tzs: '+', usd: '+' });
    const [invoiceChargeNames, setInvoiceChargeNames] = useState([]);
    const isPerShipmentCharge = (name) => invoiceChargeNames.find(n => n.name === (name || '').toUpperCase())?.per_shipment;
    const [newChargeName, setNewChargeName] = useState('');
    const [newChargePerShipment, setNewChargePerShipment] = useState(false);
    const [editingChargeName, setEditingChargeName] = useState({ id: null, value: '', per_shipment: false });
    const [invoiceNameLoading, setInvoiceNameLoading] = useState(false);

    const [statementChargeNames, setStatementChargeNames] = useState([]);
    const [newStatementChargeName, setNewStatementChargeName] = useState('');
    const [editingStatementChargeName, setEditingStatementChargeName] = useState({ id: null, value: '' });
    const [statementNameLoading, setStatementNameLoading] = useState(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [visibleSection, setVisibleSection] = useState('');

    const SECTION_OPTIONS = [
        { id: 'fund_request', text: 'FUND REQUEST' },
        { id: 'invoice_charges', text: 'CLIENT INVOICE CHARGES' },
        { id: 'invoice_names', text: 'INVOICE CHARGE NAMES' },
        { id: 'statement_names', text: 'STATEMENT CHARGE NAMES' },
        { id: 'opening_balances', text: 'CLIENT OPENING BALANCES' },
    ];

    const getDefaultNewCharge = () => ({
        charge_name: invoiceChargeNames.find(n => n.name === DEFAULT_CHARGE_NAME)?.name || invoiceChargeNames[0]?.name || DEFAULT_CHARGE_NAME,
        shipment_type: '',
        cargo_type: '',
        container_size: '',
        weight: '',
        amount: ''
    });

    const [showChargeModal, setShowChargeModal] = useState(false);
    const [newCharges, setNewCharges] = useState([{ ...getDefaultNewCharge() }]);
    const [draftCharges, setDraftCharges] = useState({});
    const [modalLoading, setModalLoading] = useState(false);

    const fetchData = async (customerId = '') => {
        setLoading(true);
        try {
            const url = customerId ? `${API_URL}?customer_id=${customerId}` : API_URL;
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load settings.');
            const data = await res.json();
            setCustomers(data.customers || []);
            setCustomersWithDefaults(data.customers_with_defaults || []);
            setCustomersWithOverrides(data.customers_with_overrides || []);
            setCharges(data.charges || []);
            const selectedIds = (data.customers || []).filter(c => c.is_fund_request).map(c => c.id);
            setFundRequestIds(selectedIds);
            if (customerId) {
                setCustomerCharges(data.customer_charges || []);
            } else {
                setCustomerCharges([]);
            }
        } catch (e) {
            toast.error(e.message || 'Failed to load settings.');
        } finally {
            setLoading(false);
        }
    };

    const fetchBalances = async (customerId = '') => {
        if (!customerId) {
            setCustomerBalances({
                previous_outstanding_balance: '',
                previous_outstanding_balance_usd: '',
                balance_brought_forward: '',
                balance_brought_forward_usd: '',
                balance_brought_forward_date: '',
                show_receivables: true
            });
            setOpeningSigns({ tzs: '+', usd: '+' });
            return;
        }
        try {
            const res = await fetch(`${API_URL}?customer_id=${customerId}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load balances.');
            const data = await res.json();
            const sc = data.selected_customer || {};
            const tzsNum = Number(sc.balance_brought_forward || 0);
            const usdNum = Number(sc.balance_brought_forward_usd || 0);
            setOpeningSigns({ tzs: tzsNum < 0 ? '-' : '+', usd: usdNum < 0 ? '-' : '+' });
            setCustomerBalances({
                previous_outstanding_balance: formatAccounting(sc.previous_outstanding_balance),
                previous_outstanding_balance_usd: formatAccounting(sc.previous_outstanding_balance_usd),
                balance_brought_forward: sc.balance_brought_forward == null ? '' : formatAccounting(Math.abs(tzsNum)),
                balance_brought_forward_usd: sc.balance_brought_forward_usd == null ? '' : formatAccounting(Math.abs(usdNum)),
                balance_brought_forward_date: sc.balance_brought_forward_date || '',
                show_receivables: sc.show_receivables !== false
            });
        } catch (e) {
            toast.error(e.message || 'Failed to load balances.');
        }
    };

    const handleBalanceCustomerChange = (val) => {
        setSelectedBalanceCustomerId(val || '');
        fetchBalances(val || '');
    };

    const fetchInvoiceChargeNames = async () => {
        try {
            const res = await fetch(INVOICE_CHARGE_NAMES_API, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load invoice charge names.');
            const data = await res.json();
            setInvoiceChargeNames(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.message || 'Failed to load invoice charge names.');
        }
    };

    const fetchStatementChargeNames = async () => {
        try {
            const res = await fetch(STATEMENT_CHARGE_NAMES_API, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load statement charge names.');
            const data = await res.json();
            setStatementChargeNames(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e.message || 'Failed to load statement charge names.');
        }
    };

    useEffect(() => {
        fetchData();
        fetchInvoiceChargeNames();
        fetchStatementChargeNames();
    }, []);

    const defaultCustomerOptions = useMemo(
        () => customersWithDefaults.filter(c => String(c.id) !== selectedBalanceCustomerId).map(c => ({ id: String(c.id), text: c.name })),
        [customersWithDefaults, selectedBalanceCustomerId]
    );
    const overrideCustomerOptions = useMemo(
        () => customersWithOverrides.filter(c => String(c.id) !== selectedBalanceCustomerId).map(c => ({ id: String(c.id), text: c.name })),
        [customersWithOverrides, selectedBalanceCustomerId]
    );
    const customerOptions = useMemo(() => customers.map(c => ({ id: String(c.id), text: c.name })), [customers]);
    const fundRequestCustomers = useMemo(() => customers.filter(c => fundRequestIds.includes(c.id)), [customers, fundRequestIds]);
    const fundRequestCustomerOptions = useMemo(() => customers.filter(c => !fundRequestIds.includes(c.id)).map(c => ({ id: String(c.id), text: c.name })), [customers, fundRequestIds]);

    const addFundRequestCustomer = (val) => {
        if (!val) return;
        const id = Number(val);
        if (!fundRequestIds.includes(id)) setFundRequestIds(prev => [...prev, id]);
        setFundRequestSelect('');
    };

    const removeFundRequestCustomer = (id) => {
        setFundRequestIds(prev => prev.filter(x => x !== id));
    };

    const saveFundRequest = async () => {
        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ fund_request_customer_ids: fundRequestIds })
            });
            if (!res.ok) throw new Error('Failed to save fund request customers.');
            toast.success('Fund request customers saved.');
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCustomerChange = (val) => {
        setSelectedCustomerId(val || '');
        fetchData(val || '');
    };

    const handleAmountChange = (chargeId, value) => {
        setCustomerCharges(prev => prev.map(c => c.charge_id === chargeId ? { ...c, amount: value } : c));
    };

    const saveCustomerCharges = async () => {
        if (!selectedCustomerId) return toast.error('Select a customer.');
        setSaving(true);
        try {
            const payload = {
                customer_id: selectedCustomerId,
                charges: customerCharges.map(c => ({ charge_id: c.charge_id, amount: c.amount }))
            };
            const res = await fetch(API_URL, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to save invoice charges.');
            toast.success('Customer invoice charges saved.');
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const resetToDefault = () => {
        setCustomerCharges(prev => prev.map(c => ({ ...c, amount: parseAmount(c.default_amount) })));
    };

    const handleBalanceChange = (field, value) => {
        setCustomerBalances(prev => ({ ...prev, [field]: value }));
    };

    const blurBalanceAmount = (field, value) => {
        const parsed = parseAccounting(value);
        const num = Number(parsed);
        setCustomerBalances(prev => ({ ...prev, [field]: isNaN(num) ? '' : formatAccounting(parsed) }));
    };

    const toggleOpeningSign = (key, sign) => {
        setOpeningSigns(prev => ({ ...prev, [key]: sign }));
    };

    const openingSignButton = (key, sign) => {
        const active = openingSigns[key] === sign;
        const neg = sign === '-';
        return (
            <button
                type="button"
                className="btn-icon"
                title={neg ? 'Negative' : 'Positive'}
                onClick={() => toggleOpeningSign(key, sign)}
                disabled={!canEdit}
                style={{
                    width: 28, height: 28, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${neg ? '#b91c1c' : '#15803d'}`, borderRadius: 4,
                    background: active ? (neg ? '#fee2e2' : '#dcfce7') : '#f9fafb',
                    color: neg ? '#b91c1c' : '#15803d', fontWeight: 700, opacity: active ? 1 : 0.45
                }}
            >
                <i className={`fa ${neg ? 'fa-plus' : 'fa-minus'}`}></i>
            </button>
        );
    };

    const blurOpeningBalance = (field, key, value) => {
        const s = String(value || '').trim();
        if (s.startsWith('-') || s.startsWith('(')) {
            setOpeningSigns(prev => ({ ...prev, [key]: '-' }));
        }
        const num = Number(parseAccounting(value));
        setCustomerBalances(prev => ({ ...prev, [field]: isNaN(num) ? '' : formatAccounting(Math.abs(num)) }));
    };

    const saveCustomerBalances = async () => {
        if (!selectedBalanceCustomerId) return toast.error('Select a customer.');
        setSaving(true);
        try {
            const tzsAbs = Math.abs(Number(parseAccounting(customerBalances.balance_brought_forward)) || 0);
            const usdAbs = Math.abs(Number(parseAccounting(customerBalances.balance_brought_forward_usd)) || 0);
            const payload = {
                customer_id: selectedBalanceCustomerId,
                customer_balance: {
                    previous_outstanding_balance: Number(parseAccounting(customerBalances.previous_outstanding_balance)) || 0,
                    previous_outstanding_balance_usd: Number(parseAccounting(customerBalances.previous_outstanding_balance_usd)) || 0,
                    balance_brought_forward: openingSigns.tzs === '-' ? -tzsAbs : tzsAbs,
                    balance_brought_forward_usd: openingSigns.usd === '-' ? -usdAbs : usdAbs,
                    balance_brought_forward_date: customerBalances.balance_brought_forward_date || null,
                    show_receivables: customerBalances.show_receivables
                }
            };
            const res = await fetch(API_URL, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to save customer balances.');
            toast.success('Customer balances saved.');
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const openChargeModal = () => {
        setShowChargeModal(true);
        setDraftCharges({});
        setNewCharges([{ ...getDefaultNewCharge() }]);
    };
    const closeChargeModal = () => setShowChargeModal(false);

    const updateDraft = (charge, field, value) => {
        setDraftCharges(prev => {
            const next = { ...(prev[charge.id] || charge), [field]: value };
            if (field === 'charge_name' && isPerShipmentCharge(value)) {
                next.shipment_type = '';
                next.cargo_type = '';
                next.container_size = '';
                next.weight = '';
            }
            return { ...prev, [charge.id]: next };
        });
    };

    const handleShipmentTypeChange = (charge, value, isNew = false, index = null) => {
        const updater = (item) => {
            const next = { ...item, shipment_type: value };
            if ((value || '').includes('AIR')) next.cargo_type = '';
            if (!(value || '').includes('AIR') && (item.cargo_type || '').toUpperCase() !== 'VEHICLE') next.weight = '';
            return next;
        };
        if (isNew) {
            setNewCharges(prev => prev.map((n, i) => i === index ? updater(n) : n));
        } else {
            setDraftCharges(prev => ({ ...prev, [charge.id]: updater(prev[charge.id] || charge) }));
        }
    };

    const handleCargoTypeChange = (charge, value, isNew = false, index = null) => {
        const updater = (item) => {
            const next = { ...item, cargo_type: value };
            if (value !== 'FCL') next.container_size = '';
            if (value !== 'VEHICLE' && !['AIR', 'AIR COURIER'].includes(value)) next.weight = '';
            return next;
        };
        if (isNew) {
            setNewCharges(prev => prev.map((n, i) => i === index ? updater(n) : n));
        } else {
            setDraftCharges(prev => ({ ...prev, [charge.id]: updater(prev[charge.id] || charge) }));
        }
    };

    const getChargeDisplay = (charge) => draftCharges[charge.id] || charge;

    const handleUpdateCharge = async (charge) => {
        setModalLoading(true);
        try {
            const res = await fetch(API_URL, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    id: charge.id,
                    charge_name: charge.charge_name,
                    shipment_type: charge.shipment_type,
                    cargo_type: charge.cargo_type,
                    container_size: charge.cargo_type === 'FCL' ? charge.container_size : '',
                    weight: (charge.cargo_type === 'VEHICLE' || (charge.shipment_type && charge.shipment_type.includes('AIR'))) ? charge.weight : '',
                    amount: charge.amount
                })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update charge.');
            }
            toast.success('Charge updated.');
            setDraftCharges(prev => { const next = { ...prev }; delete next[charge.id]; return next; });
            fetchData(selectedCustomerId);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const handleDeleteCharge = async (id) => {
        if (!window.confirm('Delete this charge?')) return;
        setModalLoading(true);
        try {
            const res = await fetch(API_URL, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                body: JSON.stringify({ id })
            });
            if (!res.ok) throw new Error('Failed to delete charge.');
            toast.success('Charge deleted.');
            fetchData(selectedCustomerId);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const addNewChargeRow = () => {
        setNewCharges(prev => [...prev, { ...getDefaultNewCharge() }]);
    };

    const removeNewChargeRow = (index) => {
        setNewCharges(prev => prev.filter((_, i) => i !== index));
    };

    const updateNewCharge = (index, field, value) => {
        setNewCharges(prev => prev.map((n, i) => {
            if (i !== index) return n;
            const next = { ...n, [field]: value };
            if (field === 'charge_name' && isPerShipmentCharge(value)) {
                next.shipment_type = '';
                next.cargo_type = '';
                next.container_size = '';
                next.weight = '';
            }
            return next;
        }));
    };

    const handleSaveNewCharges = async () => {
        const valid = newCharges.filter(n => {
            if (!n.charge_name) return false;
            if (isPerShipmentCharge(n.charge_name)) return n.amount !== '' && n.amount !== undefined && n.amount !== null;
            return n.shipment_type && n.cargo_type;
        });
        if (!valid.length) return toast.error('Enter at least one charge.');

        for (const n of valid) {
            if (isPerShipmentCharge(n.charge_name)) continue;
            if (n.cargo_type === 'FCL' && !n.container_size) return toast.error('Container size is required for FCL.');
            if (n.cargo_type === 'VEHICLE' && !n.weight) return toast.error('Vehicle option is required for VEHICLE cargo.');
            if (n.shipment_type.includes('AIR') && !n.weight) return toast.error('Weight is required for AIR shipments.');
        }

        setModalLoading(true);
        try {
            const payload = valid.map(n => ({
                charge_name: n.charge_name,
                shipment_type: n.shipment_type,
                cargo_type: n.cargo_type,
                container_size: n.cargo_type === 'FCL' ? n.container_size : '',
                weight: n.cargo_type === 'VEHICLE' ? n.weight : (n.shipment_type.includes('AIR') ? n.weight : ''),
                amount: n.amount || 0
            }));
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ charges: payload })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to add charges.');
            }
            const data = await res.json();
            toast.success(`${data.count || valid.length} charge(s) saved.`);
            setNewCharges([{ ...getDefaultNewCharge() }]);
            fetchData(selectedCustomerId);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setModalLoading(false);
        }
    };

    const handleAddInvoiceChargeName = async () => {
        const value = newChargeName.trim().toUpperCase();
        if (!value) { toast.error('Enter a charge name.'); return; }
        if (invoiceChargeNames.some(n => n.name === value)) { toast.error('Charge name already exists.'); return; }
        setInvoiceNameLoading(true);
        try {
            const res = await fetch(INVOICE_CHARGE_NAMES_API, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: value, per_shipment: newChargePerShipment })
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to add charge name.'); }
            toast.success('Charge name added.');
            setNewChargeName('');
            setNewChargePerShipment(false);
            fetchInvoiceChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setInvoiceNameLoading(false);
        }
    };

    const startEditInvoiceChargeName = (id, name, perShipment) => {
        setEditingChargeName({ id, value: name, per_shipment: !!perShipment });
    };

    const cancelEditInvoiceChargeName = () => {
        setEditingChargeName({ id: null, value: '', per_shipment: false });
    };

    const handleSaveEditInvoiceChargeName = async () => {
        const { id, value } = editingChargeName;
        if (!id) return;
        const cleanValue = value.trim().toUpperCase();
        if (!cleanValue) { toast.error('Charge name cannot be empty.'); return; }
        const existing = invoiceChargeNames.find(n => n.name === cleanValue);
        if (existing && existing.id !== id) { toast.error('Charge name already exists.'); return; }
        setInvoiceNameLoading(true);
        try {
            const res = await fetch(`${INVOICE_CHARGE_NAMES_API}${id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: cleanValue, per_shipment: editingChargeName.per_shipment })
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to update charge name.'); }
            toast.success('Charge name updated.');
            setEditingChargeName({ id: null, value: '', per_shipment: false });
            fetchInvoiceChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setInvoiceNameLoading(false);
        }
    };

    const handleDeleteInvoiceChargeName = async (id, name) => {
        if (!window.confirm(`Delete charge name "${name}"?`)) return;
        setInvoiceNameLoading(true);
        try {
            const res = await fetch(`${INVOICE_CHARGE_NAMES_API}${id}/`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (!res.ok) throw new Error('Failed to delete charge name.');
            toast.success('Charge name deleted.');
            fetchInvoiceChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setInvoiceNameLoading(false);
        }
    };

    const handleAddStatementChargeName = async () => {
        const value = newStatementChargeName.trim().toUpperCase();
        if (!value) { toast.error('Enter a statement charge name.'); return; }
        if (statementChargeNames.some(n => n.name === value)) { toast.error('Statement charge name already exists.'); return; }
        setStatementNameLoading(true);
        try {
            const res = await fetch(STATEMENT_CHARGE_NAMES_API, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: value })
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to add statement charge name.'); }
            toast.success('Statement charge name added.');
            setNewStatementChargeName('');
            fetchStatementChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setStatementNameLoading(false);
        }
    };

    const startEditStatementChargeName = (id, name) => {
        setEditingStatementChargeName({ id, value: name });
    };

    const cancelEditStatementChargeName = () => {
        setEditingStatementChargeName({ id: null, value: '' });
    };

    const handleSaveEditStatementChargeName = async () => {
        const { id, value } = editingStatementChargeName;
        if (!id) return;
        const cleanValue = value.trim().toUpperCase();
        if (!cleanValue) { toast.error('Statement charge name cannot be empty.'); return; }
        const existing = statementChargeNames.find(n => n.name === cleanValue);
        if (existing && existing.id !== id) { toast.error('Statement charge name already exists.'); return; }
        setStatementNameLoading(true);
        try {
            const res = await fetch(`${STATEMENT_CHARGE_NAMES_API}${id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: cleanValue })
            });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to update statement charge name.'); }
            toast.success('Statement charge name updated.');
            setEditingStatementChargeName({ id: null, value: '' });
            fetchStatementChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setStatementNameLoading(false);
        }
    };

    const handleDeleteStatementChargeName = async (id, name) => {
        if (!window.confirm(`Delete statement charge name "${name}"?`)) return;
        setStatementNameLoading(true);
        try {
            const res = await fetch(`${STATEMENT_CHARGE_NAMES_API}${id}/`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (!res.ok) throw new Error('Failed to delete statement charge name.');
            toast.success('Statement charge name deleted.');
            fetchStatementChargeNames();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setStatementNameLoading(false);
        }
    };

    const renderSizeWeightCell = (item, isNew, index) => {
        const name = (item.charge_name || '').toUpperCase();
        if (isPerShipmentCharge(name)) {
            return <span style={{ color: '#999' }}>-</span>;
        }
        const ct = (item.cargo_type || '').toUpperCase();
        const st = (item.shipment_type || '').toUpperCase();
        const onChange = (value) => isNew ? updateNewCharge(index, ct === 'FCL' ? 'container_size' : 'weight', value) : updateDraft(item, ct === 'FCL' ? 'container_size' : 'weight', value);

        if (ct === 'FCL') {
            return (
                <select value={item.container_size} onChange={e => onChange(e.target.value)} disabled={!canEdit || modalLoading} style={{ width: 120, fontSize: 13 }}>
                    <option value="">-- Container --</option>
                    {CONTAINER_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            );
        }
        if (ct === 'VEHICLE') {
            return (
                <select value={item.weight} onChange={e => onChange(e.target.value)} disabled={!canEdit || modalLoading} style={{ width: 120, fontSize: 13 }}>
                    <option value="">-- Vehicle --</option>
                    {VEHICLE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
            );
        }
        if (st.includes('AIR')) {
            return (
                <select value={item.weight} onChange={e => onChange(e.target.value)} disabled={!canEdit || modalLoading} style={{ width: 120, fontSize: 13 }}>
                    <option value="">-- Weight --</option>
                    {WEIGHT_OPTIONS.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
            );
        }
        return <span style={{ color: '#999' }}>-</span>;
    };

    return (
        <div className="lists-config-container">
            <div className="lists-config-header" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <h1>Accounts Settings</h1>
                <div style={{ minWidth: 260 }}>
                    <Select2Dropdown
                        options={SECTION_OPTIONS}
                        value={visibleSection}
                        onChange={val => setVisibleSection(val || '')}
                        valueKey="id"
                        displayKey="text"
                        placeholder="Select section..."
                        clearable
                    />
                </div>
            </div>

            {loading && <div className="loading">Loading...</div>}

            <div style={{ display: 'flex', gap: 24, flexWrap: 'nowrap' }}>
            {(!visibleSection || visibleSection === 'fund_request') && (
            <div className="list-card" style={{ flex: '0 0 30%', minWidth: 320, margin: '0 auto' }}>
                <div className="list-card-header">
                    <h3>Fund Request Setting</h3>
                </div>
                <div style={{ padding: '0 12px 12px' }}>
                    <Select2Dropdown
                        options={fundRequestCustomerOptions}
                        value={fundRequestSelect}
                        onChange={addFundRequestCustomer}
                        valueKey="id"
                        displayKey="text"
                        placeholder="Search and select customer..."
                        clearable={false}
                        searchable
                    />
                    <div style={{ marginTop: 12, maxHeight: 240, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: '6px 8px' }}>
                        {fundRequestCustomers.length ? (
                            fundRequestCustomers.map(c => (
                                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                    <span>{c.name}</span>
                                    <button type="button" className="btn-icon btn-icon-delete" onClick={() => removeFundRequestCustomer(c.id)} disabled={saving} title="Remove">
                                        <i className="fa fa-times" style={{ color: 'red' }}></i>
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div style={{ color: '#999', fontSize: 13 }}>No customers selected.</div>
                        )}
                    </div>
                    <div style={{ marginTop: 12, textAlign: 'right' }}>
                        <button
                            type="button"
                            className="dialog-btn dialog-btn-primary"
                            onClick={saveFundRequest}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
            )}

            {visibleSection === 'invoice_charges' && (
            <div className="list-card" style={{ flex: '1 1 70%', minWidth: 360 }}>
                <div className="list-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Client Invoice Charges</h3>
                    {canDelete && (
                        <button
                            type="button"
                            className="btn-icon"
                            title="Configure charges"
                            onClick={openChargeModal}
                            disabled={modalLoading}
                        >
                            <i className="fa fa-cog"></i>
                        </button>
                    )}
                </div>
                <div style={{ padding: '0 12px 12px' }}>
                    <div style={{ marginBottom: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Customers with Default Charges</label>
                            <Select2Dropdown
                                options={defaultCustomerOptions}
                                value={selectedCustomerId}
                                onChange={handleCustomerChange}
                                valueKey="id"
                                displayKey="text"
                                placeholder="Select customer..."
                                clearable
                                searchable
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Customers with Overridden Charges</label>
                            <Select2Dropdown
                                options={overrideCustomerOptions}
                                value={selectedCustomerId}
                                onChange={handleCustomerChange}
                                valueKey="id"
                                displayKey="text"
                                placeholder="Select customer..."
                                clearable
                                searchable
                            />
                        </div>
                    </div>
                    {selectedCustomerId ? (
                        <>
                            <table className="value-table jobs-table" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>Charge Name</th>
                                        <th>Shipment Type</th>
                                        <th>Cargo Type</th>
                                        <th>Size / Weight</th>
                                        <th style={{ width: 100 }}>Default</th>
                                        <th style={{ width: 140 }}>Customer Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customerCharges.map(c => (
                                        <tr key={c.charge_id} style={{ backgroundColor: c.amount !== '' && Number(parseAmount(c.amount || 0)) !== Number(parseAmount(c.default_amount || 0)) ? '#fff3cd' : undefined }}>
                                            <td>{c.charge_name}</td>
                                            <td>{c.shipment_type}</td>
                                            <td>{c.cargo_type}</td>
                                            <td>{c.container_size || c.weight || '-'}</td>
                                            <td>{formatAccounting(c.default_amount)}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={formatWithCommas(c.amount)}
                                                    onChange={e => handleAmountChange(c.charge_id, parseAmount(e.target.value))}
                                                    disabled={!canEdit}
                                                    style={{ width: 120, fontSize: 13 }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ marginTop: 12, textAlign: 'right' }}>
                                {customersWithOverrides.some(c => String(c.id) === selectedCustomerId) && (
                                    <button
                                        type="button"
                                        className="dialog-btn"
                                        onClick={resetToDefault}
                                        disabled={saving || !canEdit}
                                        style={{ marginRight: 8 }}
                                    >
                                        Reset to Default
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="dialog-btn dialog-btn-primary"
                                    onClick={saveCustomerCharges}
                                    disabled={saving || !canEdit}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="no-data">Select a customer to view/override invoice charges.</div>
                    )}
                </div>
            </div>
            )}
            </div>

            {(visibleSection === 'invoice_names' || visibleSection === 'statement_names') && (
            <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', margin: '24px auto 0' }}>
            {visibleSection === 'invoice_names' && (
            <div className="list-card" style={{ maxWidth: 500, flex: '1 1 400px' }}>
                <div className="list-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Invoice Charge Names</h3>
                    <span className="chip">{invoiceChargeNames.length}</span>
                </div>
                {canCreate && (
                    <div className="list-add-row" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 12px 12px' }}>
                        <input
                            type="text"
                            value={newChargeName}
                            onChange={e => setNewChargeName(e.target.value)}
                            placeholder="Add invoice charge name"
                            style={{ width: 240, fontSize: 13, textTransform: 'uppercase' }}
                            disabled={invoiceNameLoading}
                            onKeyDown={e => e.key === 'Enter' && handleAddInvoiceChargeName()}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap' }}>
                            <input
                                type="checkbox"
                                checked={newChargePerShipment}
                                onChange={e => setNewChargePerShipment(e.target.checked)}
                                disabled={invoiceNameLoading}
                            />
                            PER SHIPMENT
                        </label>
                        <button
                            type="button"
                            onClick={handleAddInvoiceChargeName}
                            disabled={invoiceNameLoading}
                            style={{ padding: '4px 10px', fontSize: 13 }}
                        >
                            Add
                        </button>
                    </div>
                )}
                <div className="value-table-wrap" style={{ padding: '0 12px 12px', maxHeight: 220 }}>
                    <table className="value-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th style={{ width: 120, textAlign: 'center' }}>PER SHIPMENT</th>
                                {(canEdit || canDelete) && <th style={{ width: 140 }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {invoiceChargeNames.length === 0 ? (
                                <tr>
                                    <td colSpan={(canEdit || canDelete) ? 3 : 2} className="empty-cell">No charge names configured.</td>
                                </tr>
                            ) : invoiceChargeNames.map(n => {
                                const isEditing = editingChargeName.id === n.id;
                                return (
                                    <tr key={n.id}>
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={editingChargeName.value}
                                                    onChange={e => setEditingChargeName(prev => ({ ...prev, value: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEditInvoiceChargeName(); if (e.key === 'Escape') cancelEditInvoiceChargeName(); }}
                                                    style={{ width: 180, fontSize: 12, textTransform: 'uppercase' }}
                                                    disabled={invoiceNameLoading}
                                                />
                                            ) : n.name}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {isEditing ? (
                                                <input
                                                    type="checkbox"
                                                    checked={editingChargeName.per_shipment}
                                                    onChange={e => setEditingChargeName(prev => ({ ...prev, per_shipment: e.target.checked }))}
                                                    disabled={invoiceNameLoading}
                                                />
                                            ) : (
                                                <input type="checkbox" checked={!!n.per_shipment} disabled style={{ cursor: 'default' }} />
                                            )}
                                        </td>
                                        {(canEdit || canDelete) && (
                                            <td>
                                                {canEdit && (
                                                    isEditing ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn-icon"
                                                                onClick={handleSaveEditInvoiceChargeName}
                                                                disabled={invoiceNameLoading}
                                                                title="Save"
                                                                style={{ marginRight: 4 }}
                                                            >
                                                                <i className="fa fa-save" style={{ color: '#28a745' }}></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-icon btn-icon-delete"
                                                                onClick={cancelEditInvoiceChargeName}
                                                                disabled={invoiceNameLoading}
                                                                title="Cancel"
                                                            >
                                                                <i className="fa fa-times" style={{ color: '#6c757d' }}></i>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn-icon"
                                                            onClick={() => startEditInvoiceChargeName(n.id, n.name, n.per_shipment)}
                                                            disabled={invoiceNameLoading}
                                                            title="Edit"
                                                            style={{ marginRight: 4 }}
                                                        >
                                                            <i className="fa fa-edit" style={{ color: '#007bff' }}></i>
                                                        </button>
                                                    )
                                                )}
                                                {canDelete && !isEditing && (
                                                    <button
                                                        type="button"
                                                        className="btn-icon btn-icon-delete"
                                                        onClick={() => handleDeleteInvoiceChargeName(n.id, n.name)}
                                                        disabled={invoiceNameLoading}
                                                        title="Delete"
                                                    >
                                                        <i className="fa fa-trash" style={{ color: 'red' }}></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {visibleSection === 'statement_names' && (
            <div className="list-card" style={{ maxWidth: 500, flex: '1 1 400px' }}>
                <div className="list-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Statement Charge Names</h3>
                    <span className="chip">{statementChargeNames.length}</span>
                </div>
                {canCreate && (
                    <div className="list-add-row" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 12px 12px' }}>
                        <input
                            type="text"
                            value={newStatementChargeName}
                            onChange={e => setNewStatementChargeName(e.target.value)}
                            placeholder="Add statement charge name"
                            style={{ width: 240, fontSize: 13, textTransform: 'uppercase' }}
                            disabled={statementNameLoading}
                            onKeyDown={e => e.key === 'Enter' && handleAddStatementChargeName()}
                        />
                        <button
                            type="button"
                            onClick={handleAddStatementChargeName}
                            disabled={statementNameLoading}
                            style={{ padding: '4px 10px', fontSize: 13 }}
                        >
                            Add
                        </button>
                    </div>
                )}
                <div className="value-table-wrap" style={{ padding: '0 12px 12px', maxHeight: 220 }}>
                    <table className="value-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                {(canEdit || canDelete) && <th style={{ width: 140 }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {statementChargeNames.length === 0 ? (
                                <tr>
                                    <td colSpan={(canEdit || canDelete) ? 2 : 1} className="empty-cell">No statement charge names configured.</td>
                                </tr>
                            ) : statementChargeNames.map(n => {
                                const isEditing = editingStatementChargeName.id === n.id;
                                return (
                                    <tr key={n.id}>
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={editingStatementChargeName.value}
                                                    onChange={e => setEditingStatementChargeName(prev => ({ ...prev, value: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEditStatementChargeName(); if (e.key === 'Escape') cancelEditStatementChargeName(); }}
                                                    style={{ width: 180, fontSize: 12, textTransform: 'uppercase' }}
                                                    disabled={statementNameLoading}
                                                />
                                            ) : n.name}
                                        </td>
                                        {(canEdit || canDelete) && (
                                            <td>
                                                {canEdit && (
                                                    isEditing ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn-icon"
                                                                onClick={handleSaveEditStatementChargeName}
                                                                disabled={statementNameLoading}
                                                                title="Save"
                                                                style={{ marginRight: 4 }}
                                                            >
                                                                <i className="fa fa-save" style={{ color: '#28a745' }}></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-icon btn-icon-delete"
                                                                onClick={cancelEditStatementChargeName}
                                                                disabled={statementNameLoading}
                                                                title="Cancel"
                                                            >
                                                                <i className="fa fa-times" style={{ color: '#6c757d' }}></i>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn-icon"
                                                            onClick={() => startEditStatementChargeName(n.id, n.name)}
                                                            disabled={statementNameLoading}
                                                            title="Edit"
                                                            style={{ marginRight: 4 }}
                                                        >
                                                            <i className="fa fa-edit" style={{ color: '#007bff' }}></i>
                                                        </button>
                                                    )
                                                )}
                                                {canDelete && !isEditing && (
                                                    <button
                                                        type="button"
                                                        className="btn-icon btn-icon-delete"
                                                        onClick={() => handleDeleteStatementChargeName(n.id, n.name)}
                                                        disabled={statementNameLoading}
                                                        title="Delete"
                                                    >
                                                        <i className="fa fa-trash" style={{ color: 'red' }}></i>
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            )}
            </div>
            )}

            {visibleSection === 'opening_balances' && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '24px auto 0' }}>
            <div className="list-card" style={{ flex: '1 1 100%', minWidth: 360 }}>
                <div className="list-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Client Opening Balances</h3>
                </div>
                <div style={{ padding: '0 12px 12px' }}>
                    <div style={{ marginBottom: 12, maxWidth: 400 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Customer</label>
                        <Select2Dropdown
                            options={customerOptions}
                            value={selectedBalanceCustomerId}
                            onChange={handleBalanceCustomerChange}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Select customer..."
                            menuPlacement="auto"
                            clearable
                            searchable
                        />
                    </div>
                    {selectedBalanceCustomerId ? (
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ display: 'none' }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Previous Outstanding Balance (TZS)</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={customerBalances.previous_outstanding_balance}
                                    onChange={e => handleBalanceChange('previous_outstanding_balance', e.target.value)}
                                    onBlur={e => blurBalanceAmount('previous_outstanding_balance', e.target.value)}
                                    disabled={!canEdit}
                                    style={{ width: 180, fontSize: 13 }}
                                />
                            </div>
                            <div style={{ display: 'none' }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Previous Outstanding Balance (USD)</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={customerBalances.previous_outstanding_balance_usd}
                                    onChange={e => handleBalanceChange('previous_outstanding_balance_usd', e.target.value)}
                                    onBlur={e => blurBalanceAmount('previous_outstanding_balance_usd', e.target.value)}
                                    disabled={!canEdit}
                                    style={{ width: 180, fontSize: 13 }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Opening Balance (TZS)</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {openingSignButton('tzs', '+')}
                                    {openingSignButton('tzs', '-')}
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={customerBalances.balance_brought_forward}
                                        onChange={e => handleBalanceChange('balance_brought_forward', e.target.value)}
                                        onBlur={e => blurOpeningBalance('balance_brought_forward', 'tzs', e.target.value)}
                                        disabled={!canEdit}
                                        style={{ width: 180, fontSize: 13 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Opening Balance (USD)</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {openingSignButton('usd', '+')}
                                    {openingSignButton('usd', '-')}
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={customerBalances.balance_brought_forward_usd}
                                        onChange={e => handleBalanceChange('balance_brought_forward_usd', e.target.value)}
                                        onBlur={e => blurOpeningBalance('balance_brought_forward_usd', 'usd', e.target.value)}
                                        disabled={!canEdit}
                                        style={{ width: 180, fontSize: 13 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Opening Balance Date</label>
                                <input
                                    type="date"
                                    value={customerBalances.balance_brought_forward_date}
                                    onChange={e => handleBalanceChange('balance_brought_forward_date', e.target.value)}
                                    disabled={!canEdit}
                                    style={{ width: 160, fontSize: 13 }}
                                />
                            </div>
                            <div style={{ display: 'none', alignItems: 'center', paddingBottom: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                                    <input
                                        type="checkbox"
                                        checked={customerBalances.show_receivables}
                                        onChange={e => handleBalanceChange('show_receivables', e.target.checked)}
                                        disabled={!canEdit}
                                    />
                                    Show Receivables
                                </label>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="dialog-btn dialog-btn-primary"
                                    onClick={saveCustomerBalances}
                                    disabled={saving || !canEdit}
                                    style={{ marginBottom: 0 }}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="no-data">Select a customer to set opening balances.</div>
                    )}
                </div>
            </div>
            </div>
            )}

            {showChargeModal && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 900, maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Configure Default Invoice Charges</h3>
                            <button type="button" className="icon-btn" onClick={closeChargeModal}>
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="dialog-body">
                            <table className="value-table jobs-table" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>Charge Name</th>
                                        <th>Shipment Type</th>
                                        <th>Cargo Type</th>
                                        <th style={{ width: 120 }}>Size / Weight</th>
                                        <th style={{ width: 110 }}>Amount</th>
                                        <th style={{ width: 120 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {charges.map(charge => {
                                        const d = getChargeDisplay(charge);
                                        const isPerShipment = isPerShipmentCharge(d.charge_name);
                                        const cargoOptions = getFilteredCargoTypes(d.shipment_type);
                                        return (
                                            <tr key={charge.id}>
                                                <td>
                                                    <select
                                                        value={d.charge_name}
                                                        onChange={e => updateDraft(charge, 'charge_name', e.target.value)}
                                                        disabled={!canEdit || modalLoading}
                                                        style={{ width: 150, fontSize: 13 }}
                                                    >
                                                        {invoiceChargeNames.map(n => <option key={n.id} value={n.name}>{n.name}</option>)}
                                                        {!invoiceChargeNames.some(n => n.name === DEFAULT_CHARGE_NAME) && (
                                                            <option value={DEFAULT_CHARGE_NAME}>{DEFAULT_CHARGE_NAME}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td>
                                                    {!isPerShipment ? (
                                                        <select
                                                            value={d.shipment_type}
                                                            onChange={e => handleShipmentTypeChange(charge, e.target.value)}
                                                            disabled={!canEdit || modalLoading}
                                                            style={{ width: 150, fontSize: 13 }}
                                                        >
                                                            <option value="">-- Shipment --</option>
                                                            {SHIPMENT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                </td>
                                                <td>
                                                    {!isPerShipment ? (
                                                        <select
                                                            value={d.cargo_type}
                                                            onChange={e => handleCargoTypeChange(charge, e.target.value)}
                                                            disabled={!canEdit || modalLoading}
                                                            style={{ width: 150, fontSize: 13 }}
                                                        >
                                                            <option value="">-- Cargo --</option>
                                                            {cargoOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                </td>
                                                <td>
                                                    {renderSizeWeightCell(d, false, null)}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={formatWithCommas(d.amount)}
                                                        onChange={e => updateDraft(charge, 'amount', parseAmount(e.target.value))}
                                                        disabled={!canEdit || modalLoading}
                                                        style={{ width: 90, fontSize: 13 }}
                                                    />
                                                </td>
                                                <td>
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            className="btn-icon"
                                                            onClick={() => handleUpdateCharge(d)}
                                                            disabled={modalLoading}
                                                            title="Save"
                                                            style={{ marginRight: 4 }}
                                                        >
                                                            <i className="fa fa-save" style={{ color: '#007bff' }}></i>
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            className="btn-icon btn-icon-delete"
                                                            onClick={() => handleDeleteCharge(charge.id)}
                                                            disabled={modalLoading}
                                                        >
                                                            <i className="fa fa-trash" style={{ color: 'red' }}></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {canCreate && newCharges.map((n, idx) => {
                                        const cargoOptions = getFilteredCargoTypes(n.shipment_type);
                                        const isPerShipmentNew = isPerShipmentCharge(n.charge_name);
                                        return (
                                            <tr key={`new-${idx}`} style={{ backgroundColor: '#f6fffe' }}>
                                                <td>
                                                    <select
                                                        value={n.charge_name}
                                                        onChange={e => updateNewCharge(idx, 'charge_name', e.target.value)}
                                                        disabled={modalLoading}
                                                        style={{ width: 150, fontSize: 13 }}
                                                    >
                                                        {invoiceChargeNames.map(n => <option key={n.id} value={n.name}>{n.name}</option>)}
                                                        {!invoiceChargeNames.some(n => n.name === DEFAULT_CHARGE_NAME) && (
                                                            <option value={DEFAULT_CHARGE_NAME}>{DEFAULT_CHARGE_NAME}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td>
                                                    {!isPerShipmentNew ? (
                                                        <select
                                                            value={n.shipment_type}
                                                            onChange={e => handleShipmentTypeChange(null, e.target.value, true, idx)}
                                                            disabled={modalLoading}
                                                            style={{ width: 150, fontSize: 13 }}
                                                        >
                                                            <option value="">-- Shipment --</option>
                                                            {SHIPMENT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                </td>
                                                <td>
                                                    {!isPerShipmentNew ? (
                                                        <select
                                                            value={n.cargo_type}
                                                            onChange={e => handleCargoTypeChange(null, e.target.value, true, idx)}
                                                            disabled={modalLoading}
                                                            style={{ width: 150, fontSize: 13 }}
                                                        >
                                                            <option value="">-- Cargo --</option>
                                                            {cargoOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                </td>
                                                <td>
                                                    {renderSizeWeightCell(n, true, idx)}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={formatWithCommas(n.amount)}
                                                        onChange={e => updateNewCharge(idx, 'amount', parseAmount(e.target.value))}
                                                        disabled={modalLoading}
                                                        style={{ width: 90, fontSize: 13 }}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn-icon btn-icon-delete"
                                                        onClick={() => removeNewChargeRow(idx)}
                                                        disabled={modalLoading || newCharges.length === 1}
                                                    >
                                                        <i className="fa fa-trash" style={{ color: 'red' }}></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {canCreate && (
                            <div className="dialog-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderTop: '1px solid #ddd' }}>
                                <button
                                    type="button"
                                    className="dialog-btn"
                                    onClick={addNewChargeRow}
                                    disabled={modalLoading}
                                >
                                    + Add Row
                                </button>
                                <div>
                                    <button
                                        type="button"
                                        className="dialog-btn"
                                        onClick={closeChargeModal}
                                        style={{ marginRight: 8 }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        className="dialog-btn dialog-btn-primary"
                                        onClick={handleSaveNewCharges}
                                        disabled={modalLoading}
                                    >
                                        Save All
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountsSettings;
